import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Search, ExternalLink, FileText, X } from 'lucide-react';
import ZEmptyState from '@/components/shared/ZEmptyState';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

// Zephyr is the deeds office: it records what exists and where. This page is
// the registry view ("where is everything"). Conversation happens in Claude
// over MCP; the in-app chat was retired in Phase 5.

const ACCENT = '#6B35B8';
const KINDS = ['brief', 'spec', 'sop', 'document', 'decision', 'artifact', 'repo', 'service', 'record'];
const STATUS_COLORS = {
  active: '#10B981',
  draft: '#9B6DD4',
  built: '#4A9CC8',
  superseded: '#4A4A6A',
};

function locationHref(loc) {
  if (!loc || loc === 'zephyr') return null;
  if (/^https?:\/\//.test(loc)) return loc;
  const repo = loc.match(/^repo:([^/]+\/[^/]+)\/(.+)$/);
  if (repo) return `https://github.com/${repo[1]}/blob/main/${repo[2]}`;
  return null;
}

export default function Zephyr() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/registry', { params: { q: query, kind } });
        setEntries(data.entries || []);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    }, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [query, kind]);

  const grouped = useMemo(() => {
    const visible = entries.filter(e => showSuperseded || e.status !== 'superseded');
    const byProject = {};
    for (const e of visible) {
      const p = e.project || 'unfiled';
      (byProject[p] = byProject[p] || []).push(e);
    }
    return Object.entries(byProject).sort(([a], [b]) => a.localeCompare(b));
  }, [entries, showSuperseded]);

  const openEntry = async (e) => {
    if (e.location !== 'zephyr') return;
    try {
      const { data } = await api.get(`/registry/${e.id}`);
      setOpen(data);
    } catch {
      setOpen(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] md:min-h-screen flex flex-col" data-testid="zephyr-page">
      <div className="p-4 md:p-6 border-b border-white/[0.06]">
        <div className="flex items-center mb-1" style={{ gap: 2 }}>
          <img src="/logo-zephyr.png" alt="Zephyr" style={{ width: 36, height: 36, clipPath: 'url(#squircle)', flexShrink: 0 }} />
          <span style={{ fontFamily: 'system-ui, sans-serif', fontSize: 28, fontWeight: 300, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#f0f0f5', lineHeight: 1 }}>EPHYR</span>
        </div>
        <p className="text-[#b0b4c0] text-xs tracking-[0.2em] uppercase font-mono" style={{ paddingLeft: 42 }}>Where everything lives</p>

        <div className="mt-5 flex items-center gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl px-3 focus-within:border-[#6B35B8] transition-colors">
          <Search size={16} strokeWidth={1.5} className="text-[#b0b4c0] shrink-0" />
          <input
            data-testid="registry-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a brief, spec, decision…"
            style={{ fontSize: '16px' }}
            className="flex-1 bg-transparent py-3 text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 focus:outline-none font-light min-w-0"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-[#b0b4c0] hover:text-[#f0f0f5]" aria-label="Clear search">
              <X size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {['', ...KINDS].map(k => (
            <button
              key={k || 'all'}
              onClick={() => setKind(k)}
              className="px-2.5 py-1 rounded-full text-[11px] font-mono tracking-wider uppercase transition-colors"
              style={{
                border: `0.5px solid ${kind === k ? 'rgba(107,53,184,0.5)' : 'rgba(255,255,255,0.06)'}`,
                background: kind === k ? 'rgba(107,53,184,0.15)' : 'transparent',
                color: kind === k ? '#9B6DD4' : '#6A6A8A',
              }}
            >{k || 'all'}</button>
          ))}
          <button
            onClick={() => setShowSuperseded(s => !s)}
            className="px-2.5 py-1 rounded-full text-[11px] font-mono tracking-wider uppercase transition-colors"
            style={{ border: '0.5px solid rgba(255,255,255,0.06)', color: showSuperseded ? '#9B6DD4' : '#454860' }}
          >{showSuperseded ? 'hide superseded' : 'show superseded'}</button>
        </div>
      </div>

      <ZLoadingBar accent={ACCENT} loading={loading} />

      <div className="flex-1 p-4 md:p-6 max-w-4xl w-full">
        {!loading && grouped.length === 0 && (
          <ZEmptyState
            mark="Z"
            accent={ACCENT}
            message={query ? 'Nothing registered matches that' : 'The registry is empty'}
            hint="Claude registers briefs, specs and decisions here with the register tool."
          />
        )}

        {grouped.map(([project, items]) => (
          <section key={project} className="mb-8">
            <h2 className="text-[#b0b4c0] text-xs tracking-[0.2em] uppercase font-mono mb-3">{project} · {items.length}</h2>
            <div className="flex flex-col gap-1.5">
              {items.map(e => {
                const href = locationHref(e.location);
                const inZephyr = e.location === 'zephyr';
                return (
                  <div
                    key={e.id}
                    data-testid={`registry-${e.id}`}
                    onClick={() => openEntry(e)}
                    className={`flex items-center gap-3 p-3 rounded-lg border border-white/[0.04] bg-white/[0.02] ${inZephyr ? 'cursor-pointer hover:bg-white/[0.04]' : ''}`}
                    style={{ opacity: e.status === 'superseded' ? 0.5 : 1 }}
                  >
                    <span className="text-[10px] font-mono uppercase tracking-wider text-[#6A6A8A] w-16 shrink-0">{e.kind}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#f0f0f5] font-light truncate">{e.title}{e.version ? <span className="text-[#6A6A8A]"> · v{e.version}</span> : null}</p>
                      <p className="text-[11px] text-[#6A6A8A] font-mono truncate">{inZephyr ? 'stored in Zephyr' : e.location}</p>
                    </div>
                    <span className="text-[10px] font-mono uppercase shrink-0" style={{ color: STATUS_COLORS[e.status] || '#6A6A8A' }}>{e.status}</span>
                    {inZephyr && <FileText size={14} strokeWidth={1.5} className="text-[#9B6DD4] shrink-0" />}
                    {href && (
                      <a href={href} target="_blank" rel="noreferrer" onClick={ev => ev.stopPropagation()} className="text-[#b0b4c0] hover:text-[#9B6DD4] shrink-0" aria-label="Open location">
                        <ExternalLink size={14} strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(null)}>
          <div className="w-full max-w-3xl mt-[6vh] bg-[#0a0a0f] border border-white/[0.06] rounded-xl p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-wider text-[#6A6A8A]">{open.kind} · {open.project}{open.version ? ` · v${open.version}` : ''}</p>
                <h3 className="text-lg text-[#f0f0f5] font-light">{open.title}</h3>
              </div>
              <button onClick={() => setOpen(null)} className="text-[#b0b4c0] hover:text-[#f0f0f5]" aria-label="Close"><X size={18} strokeWidth={1.5} /></button>
            </div>
            <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-[#d0d2dc] font-light" style={{ fontFamily: 'ui-monospace, monospace' }}>{open.body || ''}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
